/* eslint-disable @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback } from "react"
import { observer } from "mobx-react"
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Alert,
    TablePagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Modal,
    Box,
    Divider,
    AccordionSummary,
    AccordionDetails,
    Accordion,
} from "@mui/material"
import {
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material"

import { ChatbotTestExecutionService } from "src/api/services/ChatbotTestExecutionService"
import type { adminv3_ChatbotTestExecutionResponse } from "src/api/models/adminv3_ChatbotTestExecutionResponse"
import type { adminv3_ChatbotTestConfigurationResponse } from "src/api/models/adminv3_ChatbotTestConfigurationResponse"

// import { useStore } from "src/store/lib/useStore"
// import { GlobalStore } from "src/store"

interface ITestResultsProps {
    selectedConfiguration: adminv3_ChatbotTestConfigurationResponse | null
    refreshTrigger: number
}

export const TestResults: React.FC<ITestResultsProps> = observer(
    ({ selectedConfiguration, refreshTrigger }) => {
        const [executions, setExecutions] = useState<
            adminv3_ChatbotTestExecutionResponse[]
        >([])
        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)
        const [page, setPage] = useState(0)
        const [rowsPerPage, setRowsPerPage] = useState(10)
        const [totalCount, setTotalCount] = useState(0)
        const [statusFilter, setStatusFilter] = useState<string>("all")
        const [configFilter, setConfigFilter] = useState<number | "all">("all")
        const [selectedExecution, setSelectedExecution] =
            useState<adminv3_ChatbotTestExecutionResponse | null>(null)
        const [modalOpen, setModalOpen] = useState(false)

        const loadExecutions = useCallback(async () => {
            setIsLoading(true)
            setError(null)

            try {
                // Note: Access group filtering and specific filters may need to be handled differently
                // in the new API structure. For now, using basic pagination.
                const response =
                    await ChatbotTestExecutionService.postV3AdminChatbotTestExecutionList(
                        {
                            request: {
                                page_number: page + 1, // API uses 1-based page numbers
                                page_size: rowsPerPage,
                            },
                        },
                    )

                const items = Array.isArray(response.items)
                    ? (response.items as adminv3_ChatbotTestExecutionResponse[])
                    : []
                setExecutions(items)
                setTotalCount(response.total_count || 0)
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load test executions",
                )
            } finally {
                setIsLoading(false)
            }
        }, [page, rowsPerPage])

        useEffect(() => {
            void loadExecutions()
        }, [loadExecutions, refreshTrigger])

        // Reset page when filters change
        useEffect(() => {
            setPage(0)
        }, [statusFilter, configFilter])

        const handleDelete = useCallback(async (id: number) => {
            if (
                !window.confirm(
                    "Are you sure you want to delete this test execution?",
                )
            ) {
                return
            }

            try {
                await ChatbotTestExecutionService.deleteV3AdminChatbotTestExecution(
                    {
                        id: id,
                    },
                )

                // Remove from local state
                setExecutions((prev) =>
                    prev.filter((execution) => execution.id !== id),
                )
                setTotalCount((prev) => prev - 1)
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to delete test execution",
                )
            }
        }, [])

        const handleViewDetails = useCallback(
            (execution: adminv3_ChatbotTestExecutionResponse) => {
                setSelectedExecution(execution)
                setModalOpen(true)
            },
            [],
        )

        const handleCloseModal = useCallback(() => {
            setModalOpen(false)
            setSelectedExecution(null)
        }, [])

        const handleChangePage = useCallback(
            (event: unknown, newPage: number) => {
                setPage(newPage)
            },
            [],
        )

        const handleChangeRowsPerPage = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                setRowsPerPage(parseInt(event.target.value, 10))
                setPage(0)
            },
            [],
        )

        const getStatusColor = (status: string) => {
            switch (status) {
                case "completed":
                    return "success"
                case "failed":
                    return "error"
                case "running":
                    return "warning"
                default:
                    return "default"
            }
        }

        const formatCompletedAt = (
            execution: adminv3_ChatbotTestExecutionResponse,
        ) => {
            if (execution.status === "running") {
                return "In Progress"
            }
            if (
                execution.completed_at != null &&
                execution.completed_at !== "0001-01-01T00:00:00Z"
            ) {
                return new Date(execution.completed_at).toLocaleString()
            }
            return "Not Completed"
        }

        // For now, we'll calculate stats from current page only
        // TODO: Move this to server-side for accurate totals across all pages
        const stats = React.useMemo(() => {
            const total = executions.length
            const successful = executions.filter((e) =>
                Boolean(e.success),
            ).length
            const failed = executions.filter(
                (e) => !e.success && e.status === "completed",
            ).length
            const running = executions.filter(
                (e) => e.status === "running",
            ).length
            const avgDuration =
                total > 0
                    ? executions
                          .filter((e) => (e.duration_ms ?? 0) > 0)
                          .reduce((sum, e) => sum + (e.duration_ms ?? 0), 0) /
                          executions.filter((e) => (e.duration_ms ?? 0) > 0)
                              .length || 0
                    : 0

            return {
                total,
                successful,
                failed,
                running,
                successRate:
                    total > 0 ? ((successful / total) * 100).toFixed(1) : "0",
                avgDuration: Math.round(avgDuration),
            }
        }, [executions])

        return (
            <>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Test Results
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Summary Stats */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="h4" color="primary">
                                        {stats.successful}
                                    </Typography>
                                    <Typography variant="body2">
                                        Successful
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="h4" color="error">
                                        {stats.failed}
                                    </Typography>
                                    <Typography variant="body2">
                                        Failed
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography
                                        variant="h4"
                                        color="warning.main"
                                    >
                                        {stats.running}
                                    </Typography>
                                    <Typography variant="body2">
                                        Running
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="h4">
                                        {stats.successRate}%
                                    </Typography>
                                    <Typography variant="body2">
                                        Success Rate
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Filters */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status Filter</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        onChange={(e) =>
                                            setStatusFilter(e.target.value)
                                        }
                                        label="Status Filter"
                                    >
                                        <MenuItem value="all">
                                            All Statuses
                                        </MenuItem>
                                        <MenuItem value="completed">
                                            Completed
                                        </MenuItem>
                                        <MenuItem value="failed">
                                            Failed
                                        </MenuItem>
                                        <MenuItem value="running">
                                            Running
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>
                                        Configuration Filter
                                    </InputLabel>
                                    <Select
                                        value={configFilter}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            setConfigFilter(
                                                value === "all"
                                                    ? "all"
                                                    : Number(value),
                                            )
                                        }}
                                        label="Configuration Filter"
                                    >
                                        <MenuItem value="all">
                                            All Configurations
                                        </MenuItem>
                                        {selectedConfiguration != null && (
                                            <MenuItem
                                                value={selectedConfiguration.id}
                                            >
                                                {selectedConfiguration.name}
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Config ID</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Success</TableCell>
                                        <TableCell>Turns</TableCell>
                                        <TableCell>Duration (ms)</TableCell>
                                        <TableCell>Completed</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                align="center"
                                            >
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : executions.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                align="center"
                                            >
                                                No test executions found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        executions.map((execution) => (
                                            <TableRow key={execution.id}>
                                                <TableCell>
                                                    {execution.id}
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        execution.test_configuration_id
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={execution.status}
                                                        color={
                                                            getStatusColor(
                                                                execution.status ??
                                                                    "unknown",
                                                            ) as unknown as
                                                                | "default"
                                                                | "error"
                                                                | "success"
                                                                | "warning"
                                                        }
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {execution.success ? (
                                                        <Chip
                                                            label="Yes"
                                                            color="success"
                                                            size="small"
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label="No"
                                                            color="error"
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {execution.turn_count || 0}
                                                </TableCell>
                                                <TableCell>
                                                    {execution.duration_ms || 0}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCompletedAt(
                                                        execution,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleViewDetails(
                                                                execution,
                                                            )
                                                        }
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => {
                                                            if (
                                                                execution.id ==
                                                                null
                                                            ) {
                                                                return
                                                            }
                                                            void handleDelete(
                                                                execution.id,
                                                            )
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </CardContent>
                </Card>

                {/* Test Results Modal */}
                <Modal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    aria-labelledby="test-results-modal"
                    aria-describedby="test-results-details"
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "90vw",
                            maxWidth: "1200px",
                            maxHeight: "90vh",
                            bgcolor: "background.paper",
                            border: "2px solid #000",
                            boxShadow: 24,
                            p: 4,
                            overflow: "auto",
                        }}
                    >
                        {selectedExecution ? (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 3,
                                    }}
                                >
                                    <Typography variant="h5" component="h2">
                                        Test Execution #{selectedExecution.id}
                                    </Typography>
                                    <IconButton onClick={handleCloseModal}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                {/* Test Summary */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2 }}>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                Test Summary
                                            </Typography>
                                            <Typography>
                                                <strong>Status:</strong>{" "}
                                                {selectedExecution.status}
                                            </Typography>
                                            <Typography>
                                                <strong>Success:</strong>{" "}
                                                {selectedExecution.success
                                                    ? "Yes"
                                                    : "No"}
                                            </Typography>
                                            <Typography>
                                                <strong>Turn Count:</strong>{" "}
                                                {selectedExecution.turn_count ||
                                                    0}
                                            </Typography>
                                            <Typography>
                                                <strong>Duration:</strong>{" "}
                                                {selectedExecution.duration_ms ||
                                                    0}
                                                ms
                                            </Typography>
                                            <Typography>
                                                <strong>Started:</strong>{" "}
                                                {new Date(
                                                    selectedExecution.started_at ||
                                                        "",
                                                ).toLocaleString()}
                                            </Typography>
                                            <Typography>
                                                <strong>Completed:</strong>{" "}
                                                {selectedExecution != null
                                                    ? formatCompletedAt(
                                                          selectedExecution,
                                                      )
                                                    : "Not Completed"}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2 }}>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                Test Results
                                            </Typography>
                                            {selectedExecution != null &&
                                                selectedExecution.test_results !=
                                                    null && (
                                                    <Box>
                                                        <Typography>
                                                            <strong>
                                                                Completion
                                                                Reason:
                                                            </strong>{" "}
                                                            {(
                                                                selectedExecution.test_results as any
                                                            )
                                                                ?.completion_reason ||
                                                                "N/A"}
                                                        </Typography>
                                                        {selectedExecution.error_message && (
                                                            <Typography color="error">
                                                                <strong>
                                                                    Error:
                                                                </strong>{" "}
                                                                {
                                                                    selectedExecution.error_message
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* Conversation Transcripts */}
                                {selectedExecution != null &&
                                    selectedExecution.conversation_transcript !=
                                        null && (
                                        <>
                                            <Divider sx={{ my: 3 }} />
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                Conversation Transcripts
                                            </Typography>

                                            <Accordion defaultExpanded>
                                                <AccordionSummary
                                                    expandIcon={
                                                        <ExpandMoreIcon />
                                                    }
                                                >
                                                    <Typography variant="subtitle1">
                                                        Our Test Conversation
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Paper
                                                        sx={{
                                                            p: 2,
                                                            bgcolor: "#f5f5f5",
                                                            maxHeight: "400px",
                                                            overflow: "auto",
                                                        }}
                                                    >
                                                        <pre
                                                            style={{
                                                                whiteSpace:
                                                                    "pre-wrap",
                                                                fontFamily:
                                                                    "monospace",
                                                                fontSize:
                                                                    "0.875rem",
                                                            }}
                                                        >
                                                            {JSON.stringify(
                                                                (
                                                                    selectedExecution.conversation_transcript as any
                                                                )
                                                                    ?.our_conversation,
                                                                null,
                                                                2,
                                                            )}
                                                        </pre>
                                                    </Paper>
                                                </AccordionDetails>
                                            </Accordion>

                                            <Accordion sx={{ mt: 2 }}>
                                                <AccordionSummary
                                                    expandIcon={
                                                        <ExpandMoreIcon />
                                                    }
                                                >
                                                    <Typography variant="subtitle1">
                                                        Stored API Transcript
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Paper
                                                        sx={{
                                                            p: 2,
                                                            bgcolor: "#f5f5f5",
                                                            maxHeight: "400px",
                                                            overflow: "auto",
                                                        }}
                                                    >
                                                        <pre
                                                            style={{
                                                                whiteSpace:
                                                                    "pre-wrap",
                                                                fontFamily:
                                                                    "monospace",
                                                                fontSize:
                                                                    "0.875rem",
                                                            }}
                                                        >
                                                            {JSON.stringify(
                                                                (
                                                                    selectedExecution.conversation_transcript as any
                                                                )
                                                                    ?.stored_transcript,
                                                                null,
                                                                2,
                                                            )}
                                                        </pre>
                                                    </Paper>
                                                </AccordionDetails>
                                            </Accordion>
                                        </>
                                    )}
                            </>
                        ) : null}
                    </Box>
                </Modal>
            </>
        )
    },
)

export default TestResults
